import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import Stars from "@/components/Stars";

const area = "mt-2 w-full rounded-xl border border-amber-900/20 bg-white px-4 py-2.5 text-sm outline-none focus:border-amber-deep";

function ShareLinks({ links }) {
  if (!links.google_url && !links.facebook_url) return null;
  return (
    <div className="space-y-3 rounded-2xl bg-sand/60 p-6 text-left">
      <p className="text-sm text-bark">Vous pouvez aussi nous aider en partageant votre avis :</p>
      <div className="flex flex-wrap gap-2">
        {links.google_url && <a data-testid="review-share-google" href={links.google_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-amber-deep px-5 py-2.5 text-sm font-semibold text-white hover:bg-honey">Laisser un avis sur Google <ExternalLink size={14} /></a>}
        {links.facebook_url && <a data-testid="review-share-facebook" href={links.facebook_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-amber-800/25 bg-white px-5 py-2.5 text-sm font-semibold text-bark hover:border-amber-deep">Laisser un avis sur Facebook <ExternalLink size={14} /></a>}
      </div>
    </div>
  );
}

export default function ReviewPage() {
  const { token } = useParams();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ author: "", site_rating: 0, site_comment: "" });
  const [prods, setProds] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/reviews/invite/${token}`).then(({ data }) => { setInv(data); setForm((f) => ({ ...f, author: data.first_name || "" })); })
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail) || "Lien invalide"));
  }, [token]);

  useEffect(() => { if (done) window.scrollTo(0, 0); }, [done]);

  const setProd = (id, k, v) => setProds((p) => ({ ...p, [id]: { rating: 0, comment: "", ...p[id], [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.site_rating) return toast.error("Donnez une note au site (1 à 5 étoiles)");
    setBusy(true);
    try {
      const products = Object.entries(prods).filter(([, v]) => v.rating).map(([product_id, v]) => ({ product_id, ...v }));
      const { data } = await api.post(`/reviews/invite/${token}`, { ...form, products });
      setDone(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Envoi impossible");
    } finally {
      setBusy(false);
    }
  };

  const wrap = (children) => <div data-testid="review-page" className="mx-auto max-w-2xl px-5 py-16 sm:py-24">{children}</div>;
  if (error) return wrap(<p data-testid="review-error" className="text-center text-bark-soft">{error}</p>);
  if (!inv) return wrap(<p className="flex justify-center"><Loader2 className="animate-spin text-amber-deep" /></p>);
  if (done || inv.used) return wrap(
    <div data-testid="review-thanks" className="space-y-6 text-center">
      <CheckCircle2 size={56} className="mx-auto text-sage" />
      <h1 className="font-serif text-4xl font-semibold text-bark">Merci pour votre avis !</h1>
      <p className="text-bark-soft">{inv.used && !done ? "Vous avez déjà donné votre avis pour cette commande." : "Votre retour nous aide à faire grandir notre petite miellerie."}</p>
      {done?.points_status === "awarded" && <p data-testid="review-points-awarded" className="rounded-2xl bg-sage-light px-5 py-3 font-semibold text-sage">+{done.points} points fidélité ajoutés à votre compte</p>}
      {done?.points_status === "pending" && (
        <p data-testid="review-points-pending" className="rounded-2xl bg-amber-100 px-5 py-3 text-sm text-amber-900">
          <strong>{done.points} points fidélité</strong> vous attendent : <Link to="/compte/connexion" className="font-semibold underline">créez votre compte</Link> avec l'adresse e-mail de votre commande pour les recevoir.
        </p>
      )}
      <ShareLinks links={done || inv} />
    </div>,
  );

  return wrap(
    <form data-testid="review-form" onSubmit={submit} className="space-y-8">
      <div>
        <p className="eyebrow mb-2">Votre avis</p>
        <h1 className="font-serif text-4xl font-semibold text-bark sm:text-5xl">Comment s'est passée votre commande ?</h1>
        {inv.site_points + inv.product_points > 0 && (
          <p data-testid="review-points-hint" className="mt-3 text-sm text-bark-soft">En remerciement : <strong>{inv.site_points} points fidélité</strong> pour votre avis sur le site{inv.product_points > 0 && <> + <strong>{inv.product_points} points</strong> par produit noté</>} (clients avec un compte).</p>
        )}
      </div>
      <section className="space-y-2 rounded-2xl border border-amber-800/15 bg-white p-6">
        <h2 className="font-serif text-xl font-semibold text-bark">Le site</h2>
        <p className="text-sm text-bark-soft">Facilité de commande, clarté, paiement, livraison…</p>
        <Stars value={form.site_rating} onChange={(v) => setForm({ ...form, site_rating: v })} size={28} testid="review-site-stars" />
        <textarea data-testid="review-site-comment" rows={3} maxLength={1000} placeholder="Qu'avez-vous apprécié ? Que pourrions-nous améliorer ?" value={form.site_comment} onChange={(e) => setForm({ ...form, site_comment: e.target.value })} className={area} />
      </section>
      {inv.products.length > 0 && (
        <section className="space-y-5 rounded-2xl border border-amber-800/15 bg-white p-6">
          <h2 className="font-serif text-xl font-semibold text-bark">Nos produits</h2>
          {inv.products.map((p) => (
            <div key={p.product_id} data-testid={`review-product-${p.product_id}`} className="border-t border-amber-900/10 pt-4 first-of-type:border-0 first-of-type:pt-0">
              <p className="text-sm font-medium text-bark">{p.name}</p>
              <Stars value={prods[p.product_id]?.rating} onChange={(v) => setProd(p.product_id, "rating", v)} size={24} testid={`review-product-stars-${p.product_id}`} />
              <textarea data-testid={`review-product-comment-${p.product_id}`} rows={2} maxLength={1000} placeholder="Votre avis sur ce produit (facultatif)" value={prods[p.product_id]?.comment || ""} onChange={(e) => setProd(p.product_id, "comment", e.target.value)} className={area} />
            </div>
          ))}
        </section>
      )}
      <label className="block text-sm font-medium text-bark">Prénom affiché avec votre avis
        <input data-testid="review-author" required maxLength={40} value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className={area} />
      </label>
      <button data-testid="review-submit" disabled={busy} className="flex items-center gap-2 rounded-full bg-amber-deep px-8 py-3 font-semibold text-white transition-colors hover:bg-honey disabled:opacity-60">
        {busy && <Loader2 size={16} className="animate-spin" />} Envoyer mon avis
      </button>
    </form>,
  );
}
